import React, { useEffect, useState, useRef } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { FingerprintIcon, ShieldCheckIcon, ZapIcon, FileXIcon, ClockIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
const SCREENS = ['https://cdn.magicpatterns.com/uploads/nY56J73aL4D1sA2YvngMof/ChatGPT_Image_Apr_23,_2026,_01_01_07_AM.png', 'https://cdn.magicpatterns.com/uploads/5TvXjBNzCMfftEVnxfzk6F/ChatGPT_Image_Apr_23,_2026,_01_04_31_AM.png', 'https://cdn.magicpatterns.com/uploads/qeuwSo2o7dYfZBd94m6dHT/ChatGPT_Image_Apr_23,_2026,_12_46_59_AM.png'];
const FEATURES = [{
  label: 'تسجيل رقمي بالكامل',
  icon: ZapIcon
}, {
  label: 'تحقق فوري من الهوية',
  icon: ShieldCheckIcon
}, {
  label: 'بدون مستندات ورقية',
  icon: FileXIcon
}, {
  label: 'تجربة سهلة وسريعة',
  icon: ClockIcon
}];
export function DriverOnboardingSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  const [screenIndex, setScreenIndex] = useState(0);
  const [phoneActive, setPhoneActive] = useState(false);
  const phoneRef = useRef<HTMLDivElement>(null);
  // Handle click outside to deactivate phone
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (phoneRef.current && !phoneRef.current.contains(e.target as Node)) {
        setPhoneActive(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Auto-advance carousel when not manually active
  useEffect(() => {
    if (phoneActive || reduce) return;
    const timer = setInterval(() => {
      setScreenIndex(prev => (prev + 1) % SCREENS.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [phoneActive, reduce]);
  // Native keydown listener to stop propagation to SlideDeck when active
  useEffect(() => {
    const el = phoneRef.current;
    if (!el) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!phoneActive) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.stopPropagation();
        e.preventDefault();
        if (e.key === 'ArrowLeft') {
          setScreenIndex(prev => Math.max(0, prev - 1));
        } else if (e.key === 'ArrowRight') {
          setScreenIndex(prev => Math.min(SCREENS.length - 1, prev + 1));
        }
      }
    };
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [phoneActive]);
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-246">
      {/* ===== BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-247">
        <div className="absolute -top-40 -right-40 w-[60vmax] h-[60vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.08) 0%, transparent 70%)'
      }} data-id="element-248" />
        <div className="absolute -bottom-40 -left-40 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.05) 0%, transparent 70%)'
      }} data-id="element-249" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0V0zm1 1h38v38H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-250" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.9,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1.5rem,3.5vh,3rem)] px-[clamp(1.75rem,4.5vw,4.5rem)] shrink-0" data-id="element-251">
        <SharjahPoliceEmblem variant="light" data-id="element-252" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.5rem,5vh,3.5rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-253" />
      </motion.div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="relative z-20 flex-1 flex flex-col lg:flex-row items-center justify-center gap-12 px-[clamp(2rem,5vw,6rem)] pb-[clamp(4rem,8vh,6rem)]" data-id="element-254">
        {/* RIGHT SIDE: Text & Features */}
        <div className="flex-1 flex flex-col justify-center max-w-xl" data-id="element-255">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.8,
          delay: 0.2
        }} className="font-arabic-display font-black text-right mb-4" style={{
          color: '#0B1328',
          fontSize: 'clamp(2rem, 3.5vw, 3rem)'
        }} data-id="element-256">
            رحلة تسجيل السائق
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.8,
          delay: 0.3
        }} className="font-arabic-body text-right mb-10" style={{
          color: '#6E7A8F',
          fontSize: 'clamp(1.1rem, 1.5vw, 1.35rem)'
        }} data-id="element-257">
            تطبيق السائق يوفر تجربة سلسة وآمنة للتحقق من الهوية والبدء في تلقي
            المهام.
          </motion.p>

          {/* Features List */}
          <div className="flex flex-col gap-5" data-id="element-258">
            {FEATURES.map((feat, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={base ?? {
            duration: 0.6,
            delay: 0.4 + i * 0.1
          }} className="flex items-center gap-4" data-id="element-259">
                <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 border border-[#2FA99C]/10" data-id="element-260">
                  <feat.icon className="w-5 h-5 text-[#2FA99C]" data-id="element-261" />
                </div>
                <span className="font-arabic-body text-[#0B1328] font-semibold text-lg" data-id="element-262">
                  {feat.label}
                </span>
              </motion.div>)}
          </div>

          {/* UAE Pass Highlight Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.8,
          delay: 0.8
        }} className="mt-10 p-5 rounded-2xl bg-white border border-[#2FA99C]/20 shadow-lg shadow-[#2FA99C]/5 flex items-center gap-4" data-id="element-263">
            <div className="w-12 h-12 rounded-xl bg-[#2FA99C]/10 flex items-center justify-center shrink-0" data-id="element-264">
              <FingerprintIcon className="w-6 h-6 text-[#2FA99C]" data-id="element-265" />
            </div>
            <div data-id="element-266">
              <div className="font-arabic-display font-bold text-[#0B1328] text-lg mb-1" data-id="element-267">
                تكامل مباشر مع UAE Pass
              </div>
              <div className="font-arabic-body text-[#6E7A8F] text-sm" data-id="element-268">
                للتحقق من الهوية بشكل فوري وآمن
              </div>
            </div>
          </motion.div>
        </div>

        {/* LEFT SIDE: Phone Mockup (Interactive Carousel) */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        rotateY: -15
      }} animate={{
        opacity: 1,
        scale: 1,
        rotateY: -5
      }} transition={base ?? {
        duration: 1,
        delay: 0.4,
        type: 'spring',
        bounce: 0.4
      }} className="relative w-[320px] h-[650px] shrink-0 perspective-[1000px]" data-id="element-269">
          {/* Phone Frame */}
          <div ref={phoneRef} tabIndex={0} onClick={() => setPhoneActive(true)} className={`absolute inset-0 bg-white rounded-[3rem] shadow-[0_20px_50px_rgba(11,19,40,0.15),inset_0_0_0_8px_#EDF1F4,inset_0_0_0_10px_#E2E8F0] overflow-hidden flex flex-col border-[4px] border-white outline-none transition-all duration-300 cursor-pointer ${phoneActive ? 'ring-4 ring-[#2FA99C]/40 shadow-[0_0_40px_rgba(47,169,156,0.2)]' : ''}`} data-id="element-270">
            {/* App Content - Image Carousel */}
            <div className="relative flex-1 w-full h-full overflow-hidden" data-id="element-271">
              <AnimatePresence initial={false} data-id="element-272">
                {SCREENS.map((src, i) => i === screenIndex && <motion.img key={src} src={src} alt={`App Screen ${i + 1}`} className="absolute inset-0 w-full h-full object-contain bg-white" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.8,
                ease: [0.4, 0, 0.2, 1]
              }} data-id="element-273" />)}
              </AnimatePresence>

              {/* Navigation Arrows (Visible only when active) */}
              <AnimatePresence data-id="element-274">
                {phoneActive && <>
                    {screenIndex > 0 && <motion.button initial={{
                  opacity: 0,
                  scale: 0.8,
                  x: -10
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.8,
                  x: -10
                }} onClick={e => {
                  e.stopPropagation();
                  setScreenIndex(s => s - 1);
                }} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center text-[#0B1328] hover:bg-white transition-colors z-40" data-id="element-275">
                        <ChevronLeftIcon className="w-6 h-6" data-id="element-276" />
                      </motion.button>}
                    {screenIndex < SCREENS.length - 1 && <motion.button initial={{
                  opacity: 0,
                  scale: 0.8,
                  x: 10
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.8,
                  x: 10
                }} onClick={e => {
                  e.stopPropagation();
                  setScreenIndex(s => s + 1);
                }} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center text-[#0B1328] hover:bg-white transition-colors z-40" data-id="element-277">
                        <ChevronRightIcon className="w-6 h-6" data-id="element-278" />
                      </motion.button>}
                  </>}
              </AnimatePresence>

              {/* Dot Indicators */}
              <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 z-40" data-id="element-279">
                {SCREENS.map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === screenIndex ? 'bg-[#2FA99C] w-5' : 'bg-black/20 w-1.5'}`} data-id="element-280" />)}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}